import React from "react";
import { Box, Typography } from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { enUS } from "date-fns/locale/en-US";
import "react-big-calendar/lib/css/react-big-calendar.css";

const locales = {
  "en-US": enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const getDisplayContent = (content) => {
  if (!content) return "No content";
  try {
    const str = String(content).trim();
    if (str.startsWith("[") && str.endsWith("]")) {
      const escapedStr = str.replace(/\n/g, "\\n").replace(/\r/g, "\\r").replace(/\t/g, "\\t");
      const parsed = JSON.parse(escapedStr);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed[0].content || "No content";
      }
    }
  } catch (e) {
    // ignore
  }
  return content;
};

const CalendarEvent = ({ event }) => {
  const item = event.resource;
  
  const getMediaInfo = (data) => {
    let mediaObj = null;
    if (data?.media && data.media.length > 0) {
      mediaObj = data.media[0];
    } else if (data?.postDetails?.media && data.postDetails.media.length > 0) {
      mediaObj = data.postDetails.media[0];
    }
    
    if (!mediaObj) return null;
    
    const url = mediaObj.thumbnailUrl || mediaObj.fileUrl || "";
    const typeStr = (mediaObj.mediaType || "").toUpperCase();
    const fileName = mediaObj.fileName || "";
    const isVideo = typeStr === "VIDEO" || !!url.toLowerCase().match(/\.(mp4|mov|wmv|avi|mkv|webm)(\?|$)/i);
    const isDoc = !isVideo && (
      typeStr === "DOCUMENT" ||
      typeStr === "DOC" ||
      typeStr === "PDF" ||
      typeStr === "FILE" ||
      /\.(pdf|doc|docx|txt|ppt|pptx|xls|xlsx|csv)(\?|$)/i.test(url) ||
      /\.(pdf|doc|docx|txt|ppt|pptx|xls|xlsx|csv)$/i.test(fileName)
    );
    
    return {
      url,
      isVideo,
      isDoc
    };
  };

  if (item.isGrouped) {
    const { posts, onPostClick, onViewMoreClick, dateStr } = item;
    if (!posts || posts.length === 0) return null;

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", width: "100%", overflow: "hidden" }}>
        {posts.slice(0, 3).map((postItem) => {
          const getStatusColor = () => {
            if (postItem.isProcessed !== true) return "#757575"; // Not Processed
            if (postItem.isSuccess === true) return "#21AD0F";  // Success
            return "#CC2D1A";                               // Failed
          };

          const postTitle = getDisplayContent(postItem.content) || "No content";
          const mediaInfo = getMediaInfo(postItem);

          return (
            <Box 
              key={postItem.id}
              onClick={(e) => {
                e.stopPropagation();
                if (onPostClick) {
                  onPostClick(postItem);
                }
              }}
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "6px",
                cursor: "pointer",
                padding: "2px 0",
                "&:hover": {
                  "& .post-title-text": {
                    color: "#0284C7"
                  }
                }
              }}
            >
              {mediaInfo && mediaInfo.isVideo ? (
                <PlayCircleOutlineIcon sx={{ width: 24, height: 24, color: "#64748B", flexShrink: 0 }} />
              ) : mediaInfo && mediaInfo.isDoc ? (
                <InsertDriveFileIcon sx={{ width: 24, height: 24, color: "#0047AB", flexShrink: 0 }} />
              ) : mediaInfo && mediaInfo.url ? (
                <Box
                  component="img"
                  src={mediaInfo.url}
                  alt="post media"
                  onError={(e) => { e.target.style.display = 'none'; }}
                  sx={{ width: 24, height: 24, borderRadius: "4px", objectFit: "cover", flexShrink: 0 }}
                />
              ) : null}
              <Typography
                className="post-title-text"
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: "#334155",
                  transition: "color 0.2s ease-in-out",
                  flexGrow: 1
                }}
              >
                {postTitle}
              </Typography>
              <span style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: getStatusColor(),
                flexShrink: 0
              }} />
            </Box>
          );
        })}
        
        {posts.length > 3 && (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              if (onViewMoreClick) {
                onViewMoreClick(posts, dateStr, e.currentTarget);
              }
            }}
            sx={{
              fontSize: "11px",
              color: "#0284C7",
              fontWeight: 600,
              cursor: "pointer",
              padding: "2px 6px",
              marginTop: "2px",
              display: "inline-block",
              transition: "opacity 0.2s",
              "&:hover": {
                opacity: 0.8,
                textDecoration: "underline"
              }
            }}
          >
            + {posts.length - 3} more
          </Box>
        )}
      </Box>
    );
  }

  const getStatusColor = () => {
    if (item.isProcessed !== true) return "#757575"; // Not Processed
    if (item.isSuccess === true) return "#21AD0F";  // Success
    return "#CC2D1A";                               // Failed
  };

  const postTitle = getDisplayContent(item.content) || "No content";
  const mediaInfo = getMediaInfo(item);

  return (
    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "6px", height: "100%", overflow: "hidden", padding: "3px 2px" }}>
      {mediaInfo && mediaInfo.isVideo ? (
        <PlayCircleOutlineIcon sx={{ width: 24, height: 24, color: "#64748B", flexShrink: 0 }} />
      ) : mediaInfo && mediaInfo.isDoc ? (
        <InsertDriveFileIcon sx={{ width: 24, height: 24, color: "#0047AB", flexShrink: 0 }} />
      ) : mediaInfo && mediaInfo.url ? (
        <Box
          component="img"
          src={mediaInfo.url}
          alt="post media"
          onError={(e) => { e.target.style.display = 'none'; }}
          sx={{ width: 24, height: 24, borderRadius: "4px", objectFit: "cover", flexShrink: 0 }}
        />
      ) : null}
      <Typography
        className="post-title-text"
        sx={{
          fontSize: "12px",
          fontWeight: 600,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          color: "inherit",
          transition: "color 0.2s ease-in-out",
          flexGrow: 1
        }}
      >
        {postTitle}
      </Typography>
      <span style={{
        width: "8px",
        height: "8px",
        borderRadius: "50%",
        backgroundColor: getStatusColor(),
        flexShrink: 0
      }} />
    </Box>
  );
};

const transformSchedulePostsToEvents = (posts, view, callbacks = {}) => {
  if (!posts || !Array.isArray(posts)) return [];
  
  if (view === "agenda") {
    const groups = {};
    posts.forEach((item) => {
      const dateStr = new Date(item.scheduledTimeUtc + (item.scheduledTimeUtc.endsWith('Z') ? '' : 'Z')).toDateString();
      if (!groups[dateStr]) {
        groups[dateStr] = [];
      }
      groups[dateStr].push(item);
    });

    return Object.keys(groups).map((dateStr) => {
      const dayPosts = groups[dateStr];
      dayPosts.sort((a, b) => new Date(a.scheduledTimeUtc + (a.scheduledTimeUtc.endsWith('Z') ? '' : 'Z')) - new Date(b.scheduledTimeUtc + (b.scheduledTimeUtc.endsWith('Z') ? '' : 'Z')));
      
      const times = dayPosts.map(p => new Date(p.scheduledTimeUtc + (p.scheduledTimeUtc.endsWith('Z') ? '' : 'Z')).getTime());
      const minTime = new Date(Math.min(...times));
      const maxTime = new Date(Math.max(...times) + 30 * 60 * 1000);

      return {
        id: dateStr,
        title: "Multiple Posts",
        start: minTime,
        end: maxTime,
        resource: {
          dateStr: dateStr,
          posts: dayPosts,
          isGrouped: true,
          ...callbacks,
        },
      };
    });
  } else {
    return posts.map((item) => {
      const start = new Date(item.scheduledTimeUtc + (item.scheduledTimeUtc.endsWith('Z') ? '' : 'Z'));
      const end = new Date(start.getTime() + 30 * 60 * 1000);
      const title = getDisplayContent(item.content) || "No content";
      return {
        id: item.id,
        title: title,
        start: start,
        end: end,
        resource: {
          ...item,
          isGrouped: false,
          ...callbacks,
        },
      };
    });
  }
};

const BigCalendar = ({ 
  schedulePosts, 
  view, 
  onView, 
  onPostClick, 
  onViewMoreClick, 
  onSelectEvent,
  containerSx = {}
}) => {
  return (
    <Box
      sx={{
        ...containerSx,
        "& .rbc-calendar": {
          fontFamily: "inherit",
          display: "flex !important",
          flexDirection: "column !important",
          height: "100% !important",
        },
        "& .rbc-toolbar": {
          flexShrink: "0 !important",
          position: "sticky !important",
          top: "0 !important",
          zIndex: "10 !important",
          backgroundColor: "#fff !important",
          padding: "12px 0 12px 0 !important",
          margin: "0 !important",
          display: "flex !important",
          alignItems: "center !important",
          justifyContent: "space-between !important",
          borderBottom: "1px solid #E2E8F0 !important",
          marginBottom: "0 !important",
        },
        "& .rbc-month-view": {
          border: "1px solid #E2E8F0 !important",
          borderRadius: "8px",
          flex: "1 !important",
          overflowX: "auto !important",
          overflowY: "auto !important",
          isolation: "isolate",
          minHeight: 0,
        },
        "& .rbc-month-header": {
          minWidth: "650px !important",
        },
        "& .rbc-month-row": {
          borderTop: "1px solid #F1F5F9 !important",
          minWidth: "650px !important",
          zIndex: 1,
          minHeight: "100px !important",
          flex: "1 0 100px !important",
          "&:hover": {
            zIndex: 2
          }
        },
        "& .rbc-row-bg": {
          overflow: "visible !important"
        },
        "& .rbc-row-content": {
          overflow: "visible !important"
        },
        "& .rbc-row": {
          overflow: "visible !important"
        },
        "& .rbc-overlay": {
          zIndex: 9999,
          backgroundColor: "#fff !important",
          border: "1px solid #E2E8F0 !important",
          boxShadow: "0px 4px 20px rgba(0,0,0,0.08) !important",
          borderRadius: "8px !important",
          padding: "8px !important",
          minWidth: "220px",
        },
        "& .rbc-overlay-header": {
          borderBottom: "1px solid #E2E8F0 !important",
          paddingBottom: "6px !important",
          marginBottom: "6px !important",
          fontWeight: 600,
          color: "#475569",
          fontSize: "12px",
        },
        "& .rbc-day-bg + .rbc-day-bg": {
          borderLeft: "1px solid #F1F5F9 !important",
        },
        "& .rbc-date-cell": {
          paddingRight: "10px",
          paddingTop: "8px",
          paddingBottom: "4px",
          fontSize: "14px",
          fontWeight: 500,
          color: "#64748B",
        },
        "& .rbc-off-range-bg": {
          background: "#F1F5F9",
        },
        "& .rbc-today": {
          background: "#F0FDF4 !important",
        },
        /* strip the group wrapper of ALL decoration */
        "& .rbc-btn-group": {
          display: "flex !important",
          gap: "4px !important",
          border: "none !important",
          outline: "none !important",
          background: "transparent !important",
          boxShadow: "none !important",
          padding: "0 !important",
          margin: "0 !important",
          whiteSpace: "nowrap !important",
        },
        /* individual buttons */
        "& .rbc-btn-group > button": {
          color: "#475569 !important",
          fontWeight: "500 !important",
          fontSize: "13px !important",
          border: "1px solid #E2E8F0 !important",
          background: "#fff !important",
          borderRadius: "6px !important",
          padding: "5px 14px !important",
          margin: "0 !important",
          transition: "all 0.2s ease !important",
          cursor: "pointer !important",
          outline: "none !important",
          boxShadow: "none !important",
          "&:hover": {
            background: "#F8FAFC !important",
            color: "#0F172A !important",
            borderColor: "#CBD5E1 !important",
          },
          "&.rbc-active": {
            background: "#0284C7 !important",
            color: "#fff !important",
            borderColor: "#0284C7 !important",
            boxShadow: "0 2px 4px rgba(2,132,199,0.25) !important",
          },
        },
        "& .rbc-event, & .rbc-event.rbc-selected": {
          backgroundColor: "transparent !important",
          color: "#334155 !important",
          borderRadius: "0px !important",
          border: "none !important",
          padding: "0px !important",
          boxShadow: "none !important",
          outline: "none !important",
          margin: "2px 0 !important",
          cursor: "pointer",
          transition: "color 0.2s ease-in-out",
          "&:hover": {
            backgroundColor: "transparent !important",
            color: "#0284C7 !important",
          },
          "&:focus, &.rbc-selected": {
            backgroundColor: "transparent !important",
            outline: "none !important",
          }
        },
        /* Reset rbc's connected-button border overrides */
        "& .rbc-btn-group > button:first-child:not(:last-child)": {
          borderTopRightRadius: "6px !important",
          borderBottomRightRadius: "6px !important",
        },
        "& .rbc-btn-group > button:last-child:not(:first-child)": {
          borderTopLeftRadius: "6px !important",
          borderBottomLeftRadius: "6px !important",
        },
        "& .rbc-btn-group > button:not(:first-child):not(:last-child)": {
          borderRadius: "6px !important",
          borderLeft: "1px solid #E2E8F0 !important",
        },
        "& .rbc-toolbar-label": {
          fontWeight: 600,
          fontSize: "15px",
          color: "#1E293B",
          textAlign: "center",
          flex: 1,
          padding: "0 12px",
        },
        "& .rbc-event-content": {
          color: "inherit !important",
        },
        "& .rbc-today": {
          backgroundColor: "#F8FAFC",
        },
        "& .rbc-header": {
          padding: "12px 0",
          fontWeight: 600,
          fontSize: "14px",
          color: "#475569",
          borderBottom: "2px solid #E2E8F0",
        },
        "& .rbc-show-more": {
          color: "#0284C7 !important",
          fontWeight: 600,
          fontSize: "11px",
          background: "transparent !important",
          cursor: "pointer",
          padding: "2px 6px",
          display: "inline-block",
          transition: "opacity 0.2s",
          "&:hover": {
            opacity: 0.8,
            textDecoration: "underline"
          }
        }
      }}
    >
      <Calendar
        localizer={localizer}
        events={transformSchedulePostsToEvents(schedulePosts, view, {
          onPostClick,
          onViewMoreClick
        })}
        startAccessor="start"
        endAccessor="end"
        style={{ height: "100%" }}
        view={view}
        onView={onView}
        onSelectEvent={onSelectEvent}
        components={{
          event: CalendarEvent,
        }}
        views={["month", "week", "day", "agenda"]}
        defaultView="month"
        popup={true}
        showAllEvents={false}
        onShowMore={(events, date) => {
          if (onViewMoreClick) {
            const originalPosts = events.map(e => e.resource);
            // Use activeElement (the clicked +more link) as the anchor for the popover
            let anchor = document.activeElement;
            // Fallback to finding the cell if activeElement is not the link
            if (!anchor || !anchor.classList.contains('rbc-show-more')) {
              // Try to find the show-more link for this specific cell by date or just use the first one if we can't be precise
              // But activeElement is almost always correct for onClick
              const links = document.querySelectorAll('.rbc-show-more');
              anchor = Array.from(links).find(link => link === document.activeElement) || links[0] || null;
            }
            onViewMoreClick(originalPosts, date.toDateString(), anchor);
          }
        }}
      />
    </Box>
  );
};

export default BigCalendar;
